var _dec, _dec2, _class, _TruncateList;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Children, Component } from 'react';
import { debounce } from '@instructure/debounce';
import { px } from '@instructure/ui-utils';
import { testable } from '@instructure/ui-testable';
import { omitProps } from '@instructure/ui-react-utils';
import { getBoundingClientRect, getComputedStyle } from '@instructure/ui-dom-utils';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
category: components/utilities
---
**/
let TruncateList = (_dec = withStyle(generateStyle, null), _dec2 = testable(), _dec(_class = _dec2(_class = (_TruncateList = class TruncateList extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._menuTriggerRef = null;
    this._debouncedHandleResize = void 0;
    this._resizeListener = void 0;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.measureItems = () => {
      const _this$props = this.props,
        fixMenuTriggerWidth = _this$props.fixMenuTriggerWidth,
        itemSpacing = _this$props.itemSpacing;
      const itemSpacingPx = px(itemSpacing);
      const menuTriggerWidthPx = px(fixMenuTriggerWidth || this.state.menuTriggerWidth);
      let visibleItemsCount = 0;
      if (this.ref) {
        const _getBoundingClientRec = getBoundingClientRect(this.ref),
          navWidth = _getBoundingClientRec.width;
        const _getComputedStyle = getComputedStyle(this.ref),
          paddingInlineStart = _getComputedStyle.paddingInlineStart,
          paddingInlineEnd = _getComputedStyle.paddingInlineEnd;
        const navWidthWithoutPadding = navWidth - px(paddingInlineStart) - px(paddingInlineEnd);
        const itemWidths = Array.from(this.ref.getElementsByTagName('li')).map(item => {
          const _getBoundingClientRec2 = getBoundingClientRect(item),
            width = _getBoundingClientRec2.width;
          return width;
        });
        let currentWidth = 0;
        for (let i = 0; i < itemWidths.length; i++) {
          currentWidth += itemWidths[i];

          // for the last item we don't need to calculate with the menu trigger
          const maxWidth = i === itemWidths.length - 1 ? navWidthWithoutPadding : navWidthWithoutPadding - (menuTriggerWidthPx + itemSpacingPx);
          if (currentWidth <= maxWidth) {
            visibleItemsCount++;
          } else {
            break;
          }
        }
      }
      return {
        visibleItemsCount
      };
    };
    this.handleResize = () => {
      this.setState({
        isMeasuring: true
      }, () => {
        const _this$measureItems = this.measureItems(),
          visibleItemsCount = _this$measureItems.visibleItemsCount;
        if (typeof this.props.onUpdate === 'function') {
          this.props.onUpdate({
            visibleItemsCount
          });
        }
        this.setState({
          isMeasuring: false
        });
      });
    };
    this.state = {
      isMeasuring: false,
      menuTriggerWidth: void 0
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props2;
    (_this$props$makeStyle = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props2);
    const _getBoundingClientRec3 = getBoundingClientRect(this.ref),
      origWidth = _getBoundingClientRec3.width;
    this._debouncedHandleResize = debounce(this.handleResize, this.props.debounce, {
      leading: true,
      trailing: true
    });
    this._resizeListener = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (origWidth !== width) {
          var _this$_debouncedHandl;
          (_this$_debouncedHandl = this._debouncedHandleResize) === null || _this$_debouncedHandl === void 0 ? void 0 : _this$_debouncedHandl.call(this);
        }
      }
    });

    // On first render we only render the trigger to measure it
    const menuTriggerWidth = this.props.fixMenuTriggerWidth ? px(this.props.fixMenuTriggerWidth) : this.calcMenuTriggerWidth();
    this.setState({
      menuTriggerWidth
    }, () => {
      var _this$_resizeListener, _this$_debouncedHandl2;
      (_this$_resizeListener = this._resizeListener) === null || _this$_resizeListener === void 0 ? void 0 : _this$_resizeListener.observe(this.ref);
      (_this$_debouncedHandl2 = this._debouncedHandleResize) === null || _this$_debouncedHandl2 === void 0 ? void 0 : _this$_debouncedHandl2.call(this);
    });
  }
  componentDidUpdate(prevProps, prevState) {
    var _this$props$makeStyle2, _this$props3;
    (_this$props$makeStyle2 = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props3);
    if (prevProps.fixMenuTriggerWidth !== this.props.fixMenuTriggerWidth || prevProps.itemSpacing !== this.props.itemSpacing) {
      var _this$_debouncedHandl3;
      (_this$_debouncedHandl3 = this._debouncedHandleResize) === null || _this$_debouncedHandl3 === void 0 ? void 0 : _this$_debouncedHandl3.call(this);
    }
    if (this.ref && prevState.isMeasuring && prevState.isMeasuring !== this.state.isMeasuring) {
      const menuTriggerWidth = this.calcMenuTriggerWidth();
      if (menuTriggerWidth && this.state.menuTriggerWidth !== menuTriggerWidth) {
        this.setState({
          menuTriggerWidth
        }, () => {
          var _this$_debouncedHandl4;
          (_this$_debouncedHandl4 = this._debouncedHandleResize) === null || _this$_debouncedHandl4 === void 0 ? void 0 : _this$_debouncedHandl4.call(this);
        });
      }
    }
  }
  componentWillUnmount() {
    if (this._resizeListener) {
      this._resizeListener.disconnect();
    }
    if (this._debouncedHandleResize) {
      this._debouncedHandleResize.cancel();
    }
  }
  get childrenArray() {
    return Children.toArray(this.props.children);
  }
  get visibleChildren() {
    const visibleItemsCount = this.props.visibleItemsCount;
    const _this$state = this.state,
      isMeasuring = _this$state.isMeasuring,
      menuTriggerWidth = _this$state.menuTriggerWidth;

    // for the first render we need to measure the trigger width
    if (typeof menuTriggerWidth === 'undefined') {
      return [];
    }
    const visibleChildren = typeof visibleItemsCount === 'undefined' ? this.childrenArray : this.childrenArray.splice(0, visibleItemsCount);
    return isMeasuring ? this.childrenArray : visibleChildren;
  }
  get hiddenChildren() {
    const isMeasuring = this.state.isMeasuring;
    return isMeasuring ? [] : this.childrenArray.splice(this.visibleChildren.length, this.childrenArray.length);
  }
  calcMenuTriggerWidth() {
    const fixMenuTriggerWidth = this.props.fixMenuTriggerWidth;
    if (!this._menuTriggerRef) {
      return 0;
    }
    if (fixMenuTriggerWidth) {
      return px(fixMenuTriggerWidth);
    }
    const children = this._menuTriggerRef.children;
    let width = 0;
    Array.from(children).forEach(child => {
      width += getBoundingClientRect(child).width;
    });
    return width;
  }
  render() {
    const _this$props4 = this.props,
      styles = _this$props4.styles,
      className = _this$props4.className,
      style = _this$props4.style,
      renderHiddenItemMenu = _this$props4.renderHiddenItemMenu;
    const visibleChildren = this.visibleChildren,
      hiddenChildren = this.hiddenChildren;
    return _jsxs("ul", {
      ref: this.handleRef,
      ...omitProps(this.props, allowedProps),
      // we have to pass style and className
      // (e.g. if emotion style is provided, it will be passed as a className)
      className: className,
      style: style,
      css: styles === null || styles === void 0 ? void 0 : styles.truncateList,
      children: [visibleChildren.map((child, index) => {
        return _jsx("li", {
          css: styles === null || styles === void 0 ? void 0 : styles.listItem,
          children: child
        }, index);
      }), typeof renderHiddenItemMenu === 'function' && hiddenChildren && hiddenChildren.length > 0 && _jsx("li", {
        css: [styles === null || styles === void 0 ? void 0 : styles.listItem, styles === null || styles === void 0 ? void 0 : styles.menuTrigger],
        ref: e => {
          this._menuTriggerRef = e;
        },
        children: renderHiddenItemMenu(hiddenChildren)
      }, "menuTrigger")]
    });
  }
}, _TruncateList.displayName = "TruncateList", _TruncateList.componentId = 'TruncateList', _TruncateList.propTypes = propTypes, _TruncateList.allowedProps = allowedProps, _TruncateList.defaultProps = {
  itemSpacing: '0',
  debounce: 300
}, _TruncateList)) || _class) || _class);
export { TruncateList };
export default TruncateList;